import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Don't redirect if already on auth pages
      if (!window.location.pathname.includes('/login') && 
          !window.location.pathname.includes('/register') &&
          !window.location.pathname.includes('/intake')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  exchangeSession: (sessionId) => api.get('/auth/session', {
    headers: { 'X-Session-ID': sessionId }
  }),
  // Firm Switcher APIs
  getUserFirms: () => api.get('/auth/firms'),
  switchFirm: (firmId) => api.post('/auth/switch-firm', { firm_id: firmId }),
};

// Clients API
export const clientsAPI = {
  getAll: () => api.get('/clients'),
  getOne: (id) => api.get(`/clients/${id}`),
  getProfile: (id) => api.get(`/clients/${id}/profile`),
  getTimeline: (id) => api.get(`/clients/${id}/timeline`),
  getPortalView: (id) => api.get(`/clients/${id}/portal-view`),
  logActivity: (id, activityType, description, metadata = null) => 
    api.post(`/clients/${id}/activity`, { activity_type: activityType, description, metadata }),
  inviteToPortal: (id, data) => api.post(`/clients/${id}/invite`, data),
  generateCourtSummary: (id, data) => api.post(`/clients/${id}/court-summary`, data, { responseType: 'blob' }),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
};

// Firm Settings API
export const firmAPI = {
  getInfo: () => api.get('/firm'),
  getUsers: () => api.get('/firm/users'),
  getSettings: () => api.get('/firm/settings'),
  updateSettings: (data) => api.put('/firm/settings', data),
  getEnabledPracticeAreas: () => api.get('/practice-areas/enabled'),
  getChargeCodes: (practiceArea = null) => api.get('/charge-codes', { params: { practice_area: practiceArea } }),
};

// Legal Forms API
export const formsAPI = {
  // URL Scanning & Import
  scanUrl: (url) => api.post('/forms/scan-url', { url }),
  importForms: (forms) => api.post('/forms/import', forms),
  
  // Form Library
  getAll: (params = {}) => api.get('/forms', { params }),
  getById: (formId) => api.get(`/forms/${formId}`),
  update: (formId, data) => api.put(`/forms/${formId}`, data),
  delete: (formId) => api.delete(`/forms/${formId}`),
  toggleFavorite: (formId) => api.post(`/forms/${formId}/favorite`),
  download: (formId) => api.get(`/forms/${formId}/download`, { responseType: 'blob' }),
  getFields: (formId) => api.get(`/forms/${formId}/fields`),
  updateFieldMappings: (formId, mappings) => api.put(`/forms/${formId}/field-mappings`, mappings),
  
  // Case Form Instances
  addToCase: (caseId, formId) => api.post(`/cases/${caseId}/forms`, { form_id: formId }),
  getCaseForms: (caseId) => api.get(`/cases/${caseId}/forms`),
  getInstance: (instanceId) => api.get(`/form-instances/${instanceId}`),
  removeInstance: (instanceId) => api.delete(`/form-instances/${instanceId}`),
  autoFill: (instanceId) => api.post(`/form-instances/${instanceId}/auto-fill`),
  updateData: (instanceId, data) => api.put(`/form-instances/${instanceId}/data`, data),
  generate: (instanceId, flatten = true, saveToCase = true) => 
    api.post(`/form-instances/${instanceId}/generate`, null, { 
      params: { flatten, save_to_case: saveToCase },
      responseType: 'blob'
    }),
  
  // Form Packets
  generatePacket: (caseId, data) => api.post(`/cases/${caseId}/form-packet`, data, { responseType: 'blob' }),
};

// Leads API
export const leadsAPI = {
  getAll: () => api.get('/leads'),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  delete: (id) => api.delete(`/leads/${id}`),
  convert: (id) => api.post(`/leads/${id}/convert`),
};

// Cases API
export const casesAPI = {
  getAll: () => api.get('/cases'),
  getOne: (id) => api.get(`/cases/${id}`),
  getConflictCheck: (clientName, opposingParty) => api.get(`/cases/conflict-check`, { params: { client_name: clientName, opposing_party: opposingParty } }),
  checkConflict: (searchTerm) => api.get(`/conflicts/check`, { params: { query: searchTerm } }),
  create: (data) => api.post('/cases', data),
  update: (id, data) => api.put(`/cases/${id}`, data),
  delete: (id) => api.delete(`/cases/${id}`),
  assign: (id, userIds) => api.post(`/cases/${id}/assign`, { user_ids: userIds }),
  getAssignees: (id) => api.get(`/cases/${id}/assignees`),
  // AI Case Intelligence
  getAISummary: (id) => api.get(`/cases/${id}/ai-summary`),
  getAINextSteps: (id) => api.get(`/cases/${id}/ai-next-steps`),
  getAIRisks: (id) => api.get(`/cases/${id}/ai-risks`),
  getAIInsights: (id) => api.get(`/cases/${id}/ai-insights`),
};

// AI Dashboard API
export const aiDashboardAPI = {
  getDashboard: () => api.get('/ai/dashboard'),
  chat: (message, context) => api.post('/ai/chat', { message, context }),
  analyzeDocument: (documentText, analysisType) => api.post('/ai/analyze-document', { document_text: documentText, analysis_type: analysisType }),
};

// CanLII Public Records API
export const canliiAPI = {
  getDatabases: () => api.get('/canlii/databases'),
  lookupCase: (citation) => api.get('/canlii/lookup', { params: { citation } }),
  searchCases: (query, databaseId = null, limit = 10) => 
    api.post('/canlii/search', { query, database_id: databaseId, limit }),
};

// Documents API
export const documentsAPI = {
  getAll: (caseId = null) => api.get('/documents', { params: { case_id: caseId } }),
  getOne: (id) => api.get(`/documents/${id}`),
  create: (data) => api.post('/documents', data),
  upload: (data) => api.post('/documents/upload', data),
  delete: (id) => api.delete(`/documents/${id}`),
  download: (id) => `${API_BASE}/files/${id}`,
};

// Tasks API
export const tasksAPI = {
  getAll: (caseId = null, status = null) => api.get('/tasks', { params: { case_id: caseId, status } }),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  complete: (id) => api.patch(`/tasks/${id}/complete`),
  delete: (id) => api.delete(`/tasks/${id}`),
};

// Time & Billing API
export const timeAPI = {
  getEntries: (caseId = null) => api.get('/time-entries', { params: { case_id: caseId } }),
  createEntry: (data) => api.post('/time-entries', data),
  updateEntry: (entryId, data) => api.put(`/time-entries/${entryId}`, data),
  deleteEntry: (entryId) => api.delete(`/time-entries/${entryId}`),
};

// Time Entries API
export const timeEntriesAPI = {
  getAll: (caseId = null) => api.get('/time-entries', { params: { case_id: caseId } }),
  create: (data) => api.post('/time-entries', data),
  update: (id, data) => api.put(`/time-entries/${id}`, data),
  delete: (id) => api.delete(`/time-entries/${id}`),
};

export const invoicesAPI = {
  getAll: (clientId = null) => api.get('/invoices', { params: { client_id: clientId } }),
  create: (data) => api.post('/invoices', data),
  send: (id, data = {}) => api.patch(`/invoices/${id}/send`, data),
  downloadPdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
};

// Trust Accounting API
export const trustAPI = {
  getTransactions: (clientId = null) => api.get('/trust/transactions', { params: { client_id: clientId } }),
  createTransaction: (data) => api.post('/trust/transactions', data),
  getBalance: (clientId) => api.get(`/trust/balance/${clientId}`),
  getComplianceReport: () => api.get('/trust/compliance-report'),
};

// Messages API
export const messagesAPI = {
  getAll: () => api.get('/messages'),
  create: (data) => api.post('/messages', data),
  markRead: (id) => api.patch(`/messages/${id}/read`),
};

// Calendar API
export const calendarAPI = {
  getEvents: (startDate = null, endDate = null) => 
    api.get('/calendar/events', { params: { start_date: startDate, end_date: endDate } }),
  createEvent: (data) => api.post('/calendar/events', data),
  updateEvent: (id, data) => api.put(`/calendar/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/calendar/events/${id}`),
  // Google Calendar Sync
  getGoogleStatus: () => api.get('/calendar/google/status'),
  connectGoogle: (originUrl) => api.get(`/calendar/google/connect?origin_url=${encodeURIComponent(originUrl)}`),
  disconnectGoogle: () => api.delete('/calendar/google/disconnect'),
  getGoogleEvents: (timeMin = null, timeMax = null) => 
    api.get('/calendar/google/events', { params: { time_min: timeMin, time_max: timeMax } }),
  createGoogleEvent: (data) => api.post('/calendar/google/events', data),
  syncCalendar: () => api.post('/calendar/sync'),
  // Case Timeline & Progress Funnel
  getCaseTimeline: (caseId) => api.get(`/calendar/case-timeline/${caseId}`),
  getCaseProgress: (caseId) => api.get(`/calendar/case-progress/${caseId}`),
  getMasterFunnel: () => api.get('/calendar/master-funnel'),
  updateCaseStage: (caseId, stage) => api.put(`/cases/${caseId}/stage`, { stage }),
  // Jurisdiction Rules & Deadline Intelligence
  getJurisdictions: (search = null, country = null) => 
    api.get('/calendar/jurisdictions', { params: { search, country } }),
  searchJurisdictions: (search) => api.get('/calendar/jurisdictions', { params: { search } }),
  getJurisdictionDetails: (id) => api.get(`/calendar/jurisdictions/${id}`),
  calculateDeadline: (jurisdiction, deadlineType, startDate, caseId = null) => 
    api.post('/calendar/calculate-deadline', { jurisdiction, deadline_type: deadlineType, start_date: startDate, case_id: caseId }),
  // Auto-generated Case Calendars
  getCalendarTemplates: () => api.get('/calendar/templates'),
  generateCaseCalendar: (caseId, templateId, startDate = null, jurisdiction = null) =>
    api.post('/calendar/generate-case-calendar', { case_id: caseId, template_id: templateId, start_date: startDate, jurisdiction }),
  // Smart Reminders & Conflict Detection
  getConflicts: (startDate = null, endDate = null) =>
    api.get('/calendar/conflicts', { params: { start_date: startDate, end_date: endDate } }),
  generateReminders: (eventId, escalationLevel = 'auto') =>
    api.post(`/calendar/events/${eventId}/generate-reminders`, { escalation_level: escalationLevel }),
  getSmartReminders: (daysAhead = 7, includeOverdue = true) =>
    api.get('/calendar/smart-reminders', { params: { days_ahead: daysAhead, include_overdue: includeOverdue } }),
  getHolidays: (jurisdiction = 'ontario', year = 2026) =>
    api.get('/calendar/holidays', { params: { jurisdiction, year } }),
  // Time Tracking Integration
  startTimer: (eventId) => api.post(`/calendar/events/${eventId}/start-timer`),
  stopTimer: (eventId, notes = null) => api.post(`/calendar/events/${eventId}/stop-timer`, { notes }),
  getEventTimeEntries: (eventId) => api.get(`/calendar/events/${eventId}/time-entries`),
};

// Email Templates API
export const emailTemplatesAPI = {
  getAll: (category = null) => api.get('/email-templates', { params: { category } }),
  getOne: (id) => api.get(`/email-templates/${id}`),
  create: (data) => api.post('/email-templates', data),
  update: (id, data) => api.put(`/email-templates/${id}`, data),
  delete: (id) => api.delete(`/email-templates/${id}`),
  render: (id, variables) => api.post(`/email-templates/${id}/render`, variables),
  seedDefaults: () => api.get('/email-templates/defaults/seed'),
};

// AI API
export const aiAPI = {
  chat: (message, context = null) => api.post('/ai/chat', { message, context }),
  analyzeDocument: (text, type = 'summary') => 
    api.post('/ai/analyze-document', { document_text: text, analysis_type: type }),
  draftAssist: (docType, details) => 
    api.post('/ai/draft-assist', { document_type: docType, details }),
  extractDocument: (formData) => api.post('/ai/extract-document', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Payments API
export const paymentsAPI = {
  createCheckout: (invoiceId, originUrl) => 
    api.post('/payments/checkout', { invoice_id: invoiceId, origin_url: originUrl }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Subscription API
export const subscriptionAPI = {
  getTiers: () => api.get('/subscription/tiers'),
  getCurrent: () => api.get('/subscription/current'),
  getFeatures: () => api.get('/subscription/features'),
  getStatus: () => api.get('/subscription/status'),
  upgrade: (plan, billingCycle, originUrl) => 
    api.post('/subscription/upgrade', { plan, billing_cycle: billingCycle, origin_url: originUrl }),
  checkUpgradeStatus: (sessionId) => api.get(`/subscription/upgrade-status/${sessionId}`),
  startTrial: (plan) => api.post('/subscription/start-trial', { plan }),
  cancel: (reason) => api.post('/subscription/cancel', { reason }),
  checkTrialReminders: () => api.post('/subscription/trial-reminder-notifications'),
  checkTrialAccess: () => api.get('/subscription/trial-access-check'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Reports API
export const reportsAPI = {
  getOverview: () => api.get('/reports/overview'),
  getFinancial: () => api.get('/reports/financial'),
};

// Public API (no auth required)
export const publicAPI = {
  submitIntake: (data) => api.post('/public/intake', data),
  getFirmInfo: (firmId) => api.get(`/public/firm/${firmId}`),
};

// Client Portal API
export const portalAPI = {
  verifyInvite: (token) => api.get(`/portal/verify-invite/${token}`),
  setup: (data) => api.post('/portal/setup', data),
  login: (data) => api.post('/portal/login', data),
  getDashboard: () => api.get('/portal/dashboard'),
  getMyInfo: () => api.get('/portal/my-info'),
  getMyCases: () => api.get('/portal/my-cases'),
  getMyDocuments: () => api.get('/portal/my-documents'),
  getMyMessages: () => api.get('/portal/my-messages'),
  getMyInvoices: () => api.get('/portal/my-invoices'),
  sendMessage: (data) => api.post('/portal/messages', data),
  uploadDocument: (data) => api.post('/portal/documents/upload', data),
  getInvoicePayment: (id) => api.get(`/portal/invoices/${id}/pay`),
};

// KPI Dashboard API
export const kpiAPI = {
  getDashboard: () => api.get('/kpi/dashboard'),
};

// Team Management API
export const teamAPI = {
  getAll: () => api.get('/team'),
  invite: (data) => api.post('/team/invite', data),
  verifyInvite: (token) => api.get(`/team/verify-invite/${token}`),
  join: (data) => api.post('/team/join', data),
  joinWithGoogle: (data) => api.post('/team/join-with-google', data),
  updateRole: (userId, role) => api.put(`/team/${userId}/role`, null, { params: { role } }),
  updateHourlyRate: (userId, hourlyRate) => api.put(`/team/${userId}/hourly-rate`, { hourly_rate: hourlyRate }),
  remove: (userId) => api.delete(`/team/${userId}`),
  // Permissions
  getPermissions: () => api.get('/team/permissions'),
  updatePermissions: (permissions) => api.put('/team/permissions', permissions),
  // Invitations
  getInvitations: () => api.get('/team/invitations'),
  resendInvitation: (inviteId) => api.post(`/team/invitations/${inviteId}/resend`),
  cancelInvitation: (inviteId) => api.delete(`/team/invitations/${inviteId}`),
};

// Client Invitations API
export const clientInvitationsAPI = {
  getAll: () => api.get('/clients/invitations'),
  bulkInvite: (clientIds, message) => api.post('/clients/invitations/bulk', { client_ids: clientIds, message }),
  resend: (inviteId) => api.post(`/clients/invitations/${inviteId}/resend`),
  cancel: (inviteId) => api.delete(`/clients/invitations/${inviteId}`),
};

// Case Assignment API  
export const caseAssignmentAPI = {
  assign: (caseId, userIds) => api.post(`/cases/${caseId}/assign`, { user_ids: userIds }),
  getAssignees: (caseId) => api.get(`/cases/${caseId}/assignees`),
};

export default api;

// Communication API
export const communicationAPI = {
  // Email connections
  getConnections: () => api.get('/email/connections'),
  connectEmail: (provider, originUrl) => api.get(`/email/connect/${provider}?origin_url=${encodeURIComponent(originUrl)}`),
  disconnectEmail: (provider) => api.delete(`/email/disconnect/${provider}`),
  
  // Email operations
  getEmails: (folder = 'inbox', limit = 50) => api.get(`/email/messages?folder=${folder}&limit=${limit}`),
  sendEmail: (data) => api.post('/email/send', data),
  
  // Portal messages
  getPortalMessages: () => api.get('/portal-messages'),
  replyToPortalMessage: (messageId, content) => api.post('/portal-messages/reply', { message_id: messageId, content }),
};

// Team Chat API
export const chatAPI = {
  // Chat list
  getChats: () => api.get('/chat'),
  getChat: (chatId) => api.get(`/chat/${chatId}`),
  
  // Create chats
  createDM: (userId) => api.post('/chat/dm', { recipient_user_id: userId }),
  createGroup: (name, userIds) => api.post('/chat/group', { name, member_user_ids: userIds }),
  createCaseChat: (caseId) => api.post('/chat/case', { case_id: caseId }),
  
  // Messages
  getMessages: (chatId, limit = 50, before = null) => 
    api.get(`/chat/${chatId}/messages`, { params: { limit, before } }),
  sendMessage: (chatId, content, attachments = [], mentions = []) => 
    api.post(`/chat/${chatId}/messages`, { content, attachments, mentions }),
  
  // Members
  addMembers: (chatId, userIds) => api.post(`/chat/${chatId}/members`, { user_ids: userIds }),
  removeMember: (chatId, userId) => api.delete(`/chat/${chatId}/members/${userId}`),
  
  // Read status
  markAsRead: (chatId) => api.post(`/chat/${chatId}/read`),
  getUnreadCounts: () => api.get('/chat/unread/count'),
  
  // Search
  searchMessages: (query, chatId = null, limit = 20) => 
    api.get('/chat/search/messages', { params: { q: query, chat_id: chatId, limit } }),
  
  // Reactions
  addReaction: (chatId, messageId, emoji) => 
    api.post(`/chat/${chatId}/messages/${messageId}/reactions`, { emoji }),
  getReactions: (chatId, messageId) => 
    api.get(`/chat/${chatId}/messages/${messageId}/reactions`),
  removeReaction: (chatId, messageId, emoji) => 
    api.delete(`/chat/${chatId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`),
  
  // File attachments
  uploadFile: (chatId, fileData) => 
    api.post(`/chat/${chatId}/upload`, fileData),
  downloadFile: (fileId) => `${API_BASE}/chat/files/${fileId}`,
};

// MFA API
export const mfaAPI = {
  getStatus: () => api.get('/auth/mfa/status'),
  setup: () => api.post('/auth/mfa/setup'),
  verifySetup: (code) => api.post('/auth/mfa/verify-setup', { code }),
  verify: (email, code, tempToken) => api.post('/auth/mfa/verify', { email, code, temp_token: tempToken }),
  disable: (password) => api.post('/auth/mfa/disable', { password }),
};

// Document Versions API
export const documentVersionsAPI = {
  getVersions: (documentId) => api.get(`/documents/${documentId}/versions`),
  uploadVersion: (documentId, data) => api.post(`/documents/${documentId}/versions`, data),
  downloadVersion: (documentId, version) => `${API_BASE}/files/${documentId}/version/${version}`,
  restoreVersion: (documentId, version) => api.post(`/documents/${documentId}/restore/${version}`),
};

// Trust Ledger API
export const trustLedgerAPI = {
  getLedger: (clientId, startDate = null, endDate = null) => 
    api.get(`/trust/ledger/${clientId}`, { params: { start_date: startDate, end_date: endDate } }),
  getStatement: (clientId, startDate = null, endDate = null) => 
    api.get(`/trust/client/${clientId}/statement`, { params: { start_date: startDate, end_date: endDate } }),
  reconcile: (bankBalance, statementDate, notes = null) => 
    api.post('/trust/reconcile', { bank_balance: bankBalance, statement_date: statementDate, notes }),
  getReconciliations: (limit = 12) => api.get('/trust/reconciliations', { params: { limit } }),
};

// Data Retention API
export const dataRetentionAPI = {
  getStats: () => api.get('/data-retention/stats'),
  runCleanup: () => api.post('/data-retention/cleanup'),
};

// Enhanced Notifications API
export const notificationsAPI = {
  getAll: (params = {}) => api.get('/notifications', { params }),
  getGrouped: (params = {}) => api.get('/notifications/grouped', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/mark-all-read'),
  dismiss: (id) => api.put(`/notifications/${id}/dismiss`),
  clearAll: () => api.delete('/notifications/clear-all'),
};
